import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

type Row = {
  collection_date: string;
  credit_collected: number;
  credit_collected_orders: number;
  sync_date: string;
};

const B2BCreditCollections: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [startDate, setStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());

  const [syncStart, setSyncStart] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [syncEnd, setSyncEnd] = useState<Date>(new Date());
  const [isSyncing, setIsSyncing] = useState(false);

  const { data: rows } = useQuery({
    queryKey: ['b2b-daily-credit-collections', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('b2b_daily_credit_collections')
        .select('collection_date, credit_collected, credit_collected_orders, sync_date')
        .gte('collection_date', format(startDate, 'yyyy-MM-dd'))
        .lte('collection_date', format(endDate, 'yyyy-MM-dd'))
        .order('collection_date', { ascending: false })
        .limit(10000);
      if (error) throw error;
      return (data || []) as Row[];
    },
  });

  const { data: coverage } = useQuery({
    queryKey: ['b2b-credit-coverage'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('b2b_daily_credit_collections')
        .select('collection_date, sync_date')
        .order('collection_date', { ascending: true })
        .limit(10000);
      if (error) throw error;
      const r = (data || []) as Row[];
      if (!r.length) return null;
      const sortedSync = [...r].sort((a, b) => (a.sync_date < b.sync_date ? 1 : -1));
      return {
        minDate: r[0].collection_date,
        maxDate: r[r.length - 1].collection_date,
        totalDays: r.length,
        lastSyncedAt: sortedSync[0]?.sync_date,
      };
    },
  });

  const summary = rows ? {
    collected: rows.reduce((s, r) => s + Number(r.credit_collected || 0), 0),
    orders: rows.reduce((s, r) => s + Number(r.credit_collected_orders || 0), 0),
    days: rows.length,
  } : null;

  const handleSync = async () => {
    if (syncStart > syncEnd) {
      toast({ title: 'Invalid Range', description: 'Start must be before end', variant: 'destructive' });
      return;
    }
    setIsSyncing(true);
    try {
      const { error } = await supabase.functions.invoke('b2b-delivery-sync', {
        body: {
          startDate: format(syncStart, 'yyyy-MM-dd'),
          endDate: format(syncEnd, 'yyyy-MM-dd'),
          skipInventory: true,
        },
      });
      if (error) throw error;
      toast({ title: 'Credit Collections Synced', description: `Range ${format(syncStart, 'MMM dd')} – ${format(syncEnd, 'MMM dd')}` });
      queryClient.invalidateQueries({ queryKey: ['b2b-daily-credit-collections'] });
      queryClient.invalidateQueries({ queryKey: ['b2b-credit-coverage'] });
    } catch (err: any) {
      toast({ title: 'Sync Failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsSyncing(false);
    }
  };

  const fmtETB = (n: number) => `ETB ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold">B2B Credit Collections</h1>
          <p className="text-sm text-muted-foreground">
            Daily credit collected from B2B customers (payments received against previously delivered credit orders).
          </p>
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">View From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">View To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <Badge variant="outline">
                {summary?.days ?? 0} day{(summary?.days ?? 0) === 1 ? '' : 's'} in current filter
              </Badge>
              {coverage && (
                <Badge variant="secondary" title={`Last synced: ${format(new Date(coverage.lastSyncedAt), 'PPpp')}`}>
                  DB coverage: {format(new Date(coverage.minDate), 'MMM dd, yyyy')} – {format(new Date(coverage.maxDate), 'MMM dd, yyyy')} ({coverage.totalDays} days)
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Sync From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(syncStart, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={syncStart} onSelect={(d) => d && setSyncStart(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Sync To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(syncEnd, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={syncEnd} onSelect={(d) => d && setSyncEnd(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <Button onClick={handleSync} size="sm" disabled={isSyncing}>
                <RefreshCw className={cn('mr-2 h-4 w-4', isSyncing && 'animate-spin')} />
                {isSyncing ? 'Syncing...' : 'Sync Credit Collections for Range'}
              </Button>
              <span className="text-xs text-muted-foreground">
                Uses the B2B delivery MCP tool (skips inventory writes).
              </span>
            </div>
          </CardContent>
        </Card>

        {summary && summary.days > 0 ? (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Credit Collected</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fmtETB(summary.collected)}</div>
                  <p className="text-xs text-muted-foreground">In current page filter</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Orders Paid</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summary.orders.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Distinct credit orders settled</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Collection / Day</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {fmtETB(summary.days ? summary.collected / summary.days : 0)}
                  </div>
                  <p className="text-xs text-muted-foreground">Across {summary.days} day{summary.days === 1 ? '' : 's'}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Credit Collected (ETB)</TableHead>
                      <TableHead className="text-right">Orders Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows!.map((r) => (
                      <TableRow key={r.collection_date}>
                        <TableCell>{format(new Date(r.collection_date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell className="text-right">{Number(r.credit_collected).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-right">{Number(r.credit_collected_orders).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Outstanding Receivables (Aging)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Aging by customer will appear here once the upstream B2B team ships the <code>get_credit_orders</code> MCP tool
                  (spec: <code>docs/b2b-receivables-spec.md</code>). We already have the local table <code>b2b_credit_orders</code> ready to receive it.
                </p>
              </CardContent>
            </Card>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No credit collection data for this range. Click "Sync Credit Collections for Range" to fetch from the MCP.
          </p>
        )}
      </div>
    </Layout>
  );
};

export default B2BCreditCollections;
